import { useState } from 'react';
import {
  Box,
  Typography,
  List,
  Button,
  IconButton,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { AsyncAutocomplete, BrowseViewResponseDto } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { apiFetch } from '@/core';
import { AuditorCompetence } from '@/common';
import env from '@/env';
import { useCreatePageStore } from './useCreatePageStore';

const getCompetencesForCurrentAuditor = async ({
  page,
  pageSize,
  search,
}: {
  page: number;
  pageSize: number;
  search: string;
}): Promise<BrowseViewResponseDto<AuditorCompetence>> => {
  const fetchResponse = await apiFetch(
    `${env.VITE_API_URL}/pb/s/api/sqo-portal/auditor-comps/me?${new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search: search.toString(),
    })}`,
    { method: 'GET' }
  );

  const response = (await fetchResponse.json()) as BrowseViewResponseDto<AuditorCompetence>;
  return response;
};

const CompetencesCreate = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const [showAddInput, setShowAddInput] = useState(false);
  const [selectedCompetence, setSelectedCompetence] = useState<AuditorCompetence | null>(null);
  const createPageStore = useCreatePageStore();

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {createPageStore.competences.length > 0 ? (
          <List>
            {createPageStore.competences.map((competence) => (
              <ListItem
                sx={{ px: 0 }}
                key={competence.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() =>
                      confirm({
                        description: `${t('This will premamently delete', {
                          defaultValue: 'This will premamently delete',
                        })} ${competence.name}`,
                      }).then(() =>
                        createPageStore.setCompetences(
                          createPageStore.competences.filter(
                            (competenceInStore) => competenceInStore.id !== competence.id
                          )
                        )
                      )
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <LightbulbIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={competence.name}
                  secondary={competence.type?.subTypeShowAs || competence.type?.typeShowAs || '/'}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="subtitle1">
            {t('No competences', { defaultValue: 'No competences' })}
          </Typography>
        )}
      </Box>
      {showAddInput ? (
        <>
          <AsyncAutocomplete<AuditorCompetence>
            id="auditor-competence"
            label={t('Competence', { defaultValue: 'Competence' })}
            getData={async (search) => {
              const response = await getCompetencesForCurrentAuditor({
                page: 1,
                pageSize: 1000,
                search,
              });
              return response.results;
            }}
            getOptionLabel={(option) => option.name || 'N/A'}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            selectedOption={selectedCompetence}
            setSelectedOption={setSelectedCompetence}
            fullWidth
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (!selectedCompetence) {
                return;
              }

              const competenceAlreadyInStore = createPageStore.competences.find(
                (competenceInStore) => competenceInStore.id === selectedCompetence.id
              );

              if (competenceAlreadyInStore) {
                enqueueSnackbar(
                  t('Competence is already in the list.', {
                    defaultValue: 'Competence is already in the list.',
                  }),
                  { variant: 'error' }
                );
                return;
              }

              createPageStore.setCompetences([...createPageStore.competences, selectedCompetence]);
              setShowAddInput(false);
              setSelectedCompetence(null);
            }}
            sx={{ mt: 2 }}
            fullWidth
          >
            {t('Add', { defaultValue: 'Add' })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => setShowAddInput(false)}
            sx={{ mt: 1 }}
            fullWidth
          >
            {t('Cancel', { defaultValue: 'Cancel' })}
          </Button>
        </>
      ) : (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowAddInput(true)}
          fullWidth
        >
          {t('Add new competence', { defaultValue: 'Add new competence' })}
        </Button>
      )}
    </>
  );
};

export default CompetencesCreate;
